import { useState, useEffect, useCallback } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BookOpen, Upload, Building2, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { FolderTree } from "@/components/knowledge-base/FolderTree";
import { DocumentGrid } from "@/components/knowledge-base/DocumentGrid";
import { CreateFolderDialog } from "@/components/knowledge-base/CreateFolderDialog";
import { UploadDocumentDialog } from "@/components/knowledge-base/UploadDocumentDialog";
import { CompanyProfileTab } from "@/components/knowledge-base/CompanyProfileTab";
import {
  getFolders,
  getDocuments,
  createFolder,
  deleteFolder,
  deleteDocument,
  getDocumentDownloadUrl,
  uploadDocument,
  getCompanyKnowledgeData,
  seedDefaultFolders,
  type KBFolder,
  type KBDocument,
} from "@/lib/knowledge-base-service";

export default function KnowledgeBase() {
  const { toast } = useToast();
  const { profile } = useAuth();
  const companyId = profile?.company_id;

  // Folder state
  const [folders, setFolders] = useState<KBFolder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null | undefined>(undefined);
  const [foldersLoading, setFoldersLoading] = useState(true);

  // Document state
  const [documents, setDocuments] = useState<KBDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  // Company profile
  const [profileAnswers, setProfileAnswers] = useState<Record<string, string> | null>(null);

  // Dialog state
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<KBFolder | null>(null);

  // Polling interval for processing documents
  const [pollInterval, setPollInterval] = useState<ReturnType<typeof setInterval> | null>(null);

  // ─── Load folders + seed defaults on mount ───────────────────────────────

  const loadFolders = useCallback(async () => {
    if (!companyId) return;
    try {
      const data = await getFolders(companyId);
      setFolders(data);
      return data;
    } catch (e: any) {
      toast({ title: "Failed to load folders", description: e.message, variant: "destructive" });
      return [];
    }
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !profile?.id) return;
    setFoldersLoading(true);

    const init = async () => {
      try {
        await seedDefaultFolders(companyId, profile.id);
        await loadFolders();
      } finally {
        setFoldersLoading(false);
      }
    };
    init();
  }, [companyId, profile?.id]);

  // ─── Load documents whenever folder selection changes ─────────────────────

  const loadDocuments = useCallback(async () => {
    if (!companyId) return;
    setDocumentsLoading(true);
    try {
      const data = await getDocuments(companyId, selectedFolderId);
      setDocuments(data);

      // If any docs are still processing, keep polling
      const hasProcessing = data.some(d => d.status === 'processing' || d.status === 'uploading');
      if (hasProcessing && !pollInterval) {
        const id = setInterval(async () => {
          const refreshed = await getDocuments(companyId, selectedFolderId);
          setDocuments(refreshed);
          const stillProcessing = refreshed.some(d => d.status === 'processing' || d.status === 'uploading');
          if (!stillProcessing) {
            clearInterval(id);
            setPollInterval(null);
          }
        }, 3000);
        setPollInterval(id);
      }
    } catch (e: any) {
      toast({ title: "Failed to load documents", description: e.message, variant: "destructive" });
    } finally {
      setDocumentsLoading(false);
    }
  }, [companyId, selectedFolderId]);

  useEffect(() => {
    loadDocuments();
    return () => { if (pollInterval) clearInterval(pollInterval); };
  }, [selectedFolderId, companyId]);

  // ─── Load company profile ─────────────────────────────────────────────────

  useEffect(() => {
    if (!companyId) return;
    getCompanyKnowledgeData(companyId)
      .then(data => {
        if (data) {
          setProfileAnswers(data);
        } else {
          // Try to migrate from localStorage
          const legacy = localStorage.getItem('company_knowledge');
          if (legacy) {
            // Legacy is a markdown string, not answers — we can't auto-parse it back.
            // Just set empty answers so the user fills the form.
          }
        }
      })
      .catch(console.error);
  }, [companyId]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleCreateFolder = async (name: string, description?: string) => {
    if (!companyId || !profile?.id) return;
    try {
      const folder = await createFolder(companyId, name, profile.id, null, description);
      setFolders(prev => [...prev, folder].sort((a, b) => a.name.localeCompare(b.name)));
      toast({ title: "Folder created", description: name });
    } catch (e: any) {
      toast({ title: "Failed to create folder", description: e.message, variant: "destructive" });
      throw e;
    }
  };

  const handleDeleteFolder = async () => {
    if (!deleteFolderTarget) return;
    try {
      await deleteFolder(deleteFolderTarget.id);
      setFolders(prev => prev.filter(f => f.id !== deleteFolderTarget.id));
      if (selectedFolderId === deleteFolderTarget.id) setSelectedFolderId(undefined);
      toast({ title: "Folder deleted" });
    } catch (e: any) {
      toast({ title: "Failed to delete folder", description: e.message, variant: "destructive" });
    } finally {
      setDeleteFolderTarget(null);
    }
  };

  const handleUpload = async (
    file: File,
    meta: { folderId?: string | null; jurisdiction?: string; regulationType?: string }
  ) => {
    if (!companyId || !profile?.id) return;
    try {
      const doc = await uploadDocument(file, {
        companyId,
        uploadedBy: profile.id,
        folderId: meta.folderId,
        jurisdiction: meta.jurisdiction,
        regulationType: meta.regulationType,
      });

      // If we're in a folder view that matches, add the doc
      if (
        selectedFolderId === undefined ||
        selectedFolderId === meta.folderId ||
        (selectedFolderId === null && !meta.folderId)
      ) {
        setDocuments(prev => [doc, ...prev]);
      }

      toast({
        title: "Document uploaded",
        description: doc.extracted_text
          ? "Indexing in progress — it will be ready for the AI agent shortly."
          : "File saved. Text extraction was not possible for this file type.",
      });

      // Start polling to catch when it becomes ready
      if (doc.extracted_text && !pollInterval) {
        const id = setInterval(async () => {
          const refreshed = await getDocuments(companyId, selectedFolderId);
          setDocuments(refreshed);
          const stillProcessing = refreshed.some(d => d.status === 'processing' || d.status === 'uploading');
          if (!stillProcessing) {
            clearInterval(id);
            setPollInterval(null);
          }
        }, 3000);
        setPollInterval(id);
      }
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
      throw e;
    }
  };

  const handleDeleteDocument = async (doc: KBDocument) => {
    try {
      await deleteDocument(doc);
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
      toast({ title: "Document deleted" });
    } catch (e: any) {
      toast({ title: "Failed to delete document", description: e.message, variant: "destructive" });
    }
  };

  const handleDownloadDocument = async (doc: KBDocument) => {
    if (!doc.file_path) return;
    try {
      const url = await getDocumentDownloadUrl(doc.file_path);
      window.open(url, '_blank');
    } catch (e: any) {
      toast({ title: "Download failed", description: e.message, variant: "destructive" });
    }
  };

  // ─── Derived values ────────────────────────────────────────────────────────

  const selectedFolder = folders.find(f => f.id === selectedFolderId);
  const totalDocuments = documents.length;
  const readyDocuments = documents.filter(d => d.status === 'ready').length;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-medium text-foreground">Company Knowledge Base</h1>
            {readyDocuments > 0 && (
              <Badge variant="secondary" className="text-xs">
                {readyDocuments} {readyDocuments === 1 ? 'document' : 'documents'} indexed
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Upload regulation PDFs and compliance documents. The AI policy agent draws from this knowledge base when drafting policies.
          </p>
        </div>
        <Button onClick={() => setUploadOpen(true)} className="gap-2 shrink-0">
          <Upload className="h-4 w-4" />
          Upload document
        </Button>
      </div>

      {/* Main tabs */}
      <Tabs defaultValue="documents">
        <TabsList>
          <TabsTrigger value="documents" className="gap-2">
            <BookOpen className="h-4 w-4" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="profile" className="gap-2">
            <Building2 className="h-4 w-4" />
            Company Profile
          </TabsTrigger>
        </TabsList>

        {/* ── Documents tab ── */}
        <TabsContent value="documents" className="mt-6">
          <div className="flex gap-6">
            {/* Left: Folder tree */}
            <div className="w-56 shrink-0">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-2">
                Folders
              </p>
              {foldersLoading ? (
                <div className="space-y-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-9 rounded-lg bg-muted/40 animate-pulse" />
                  ))}
                </div>
              ) : (
                <FolderTree
                  folders={folders}
                  selectedFolderId={selectedFolderId}
                  onSelectFolder={setSelectedFolderId}
                  onCreateFolder={() => setCreateFolderOpen(true)}
                  onDeleteFolder={folder => setDeleteFolderTarget(folder)}
                />
              )}
            </div>

            {/* Right: Documents */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-medium text-foreground">
                  {selectedFolder ? selectedFolder.name : "All documents"}
                  {selectedFolder?.description && (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      {selectedFolder.description}
                    </span>
                  )}
                </p>
                <button
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  onClick={loadDocuments}
                >
                  <RefreshCw className="h-3 w-3" />
                  Refresh
                </button>
              </div>

              <DocumentGrid
                documents={documents}
                loading={documentsLoading}
                onDelete={handleDeleteDocument}
                onDownload={handleDownloadDocument}
                onUploadClick={() => setUploadOpen(true)}
              />
            </div>
          </div>
        </TabsContent>

        {/* ── Company Profile tab ── */}
        <TabsContent value="profile" className="mt-6">
          {companyId && (
            <CompanyProfileTab
              companyId={companyId}
              initialAnswers={profileAnswers}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <CreateFolderDialog
        open={createFolderOpen}
        onOpenChange={setCreateFolderOpen}
        onCreate={handleCreateFolder}
      />

      <UploadDocumentDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        folders={folders}
        defaultFolderId={typeof selectedFolderId === 'string' ? selectedFolderId : null}
        onUpload={handleUpload}
      />

      <AlertDialog open={!!deleteFolderTarget} onOpenChange={open => !open && setDeleteFolderTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete folder?</AlertDialogTitle>
            <AlertDialogDescription>
              The folder <strong>{deleteFolderTarget?.name}</strong> will be deleted. Documents inside will be moved to unfiled. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteFolder}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
